import { useAuth } from "@/auth";
import { useAppSession } from "@/auth/AppSessionContext";
import { isLoggedIn } from "@/auth/utils";
import { FLAGS, FLAG_DEFAULTS } from "@/feature-flags";
import { WindowKind, instanceId, useDesktop } from "@/overlay/components/window";
import { mapLocaleToContentLanguage } from "@/repository/mapper/LanguageMapper";
import { useLingui } from "@lingui/react";
import { useFlag } from "@openfeature/react-sdk";
import { useEffect, useRef } from "react";
import { useChangelogPopup } from "./useChangelogPopup";

const whatsNewWindowId = instanceId(WindowKind.WhatsNewWindow);

export function useWhatsNewWindow() {
  const { session } = useAuth();
  const loggedIn = isLoggedIn(session);
  const { sessionActivated } = useAppSession();
  const { i18n } = useLingui();
  const { value: isChangelogActive } = useFlag("changelog", false);
  const { value: currentChangelogVersion } = useFlag(
    FLAGS.CURRENT_CHANGELOG_VERSION,
    FLAG_DEFAULTS[FLAGS.CURRENT_CHANGELOG_VERSION],
  );
  const contentLanguage = mapLocaleToContentLanguage(i18n.locale);
  const { loading, shouldShow, version, incrementCount, dontShowAgain } = useChangelogPopup(
    contentLanguage,
    currentChangelogVersion,
  );
  const desktop = useDesktop();
  const dispatch = desktop.dispatch;
  const isVisible = desktop.isWindowVisible(whatsNewWindowId);
  const customProps = desktop.windowMap().get(whatsNewWindowId)?.state.customProps;
  const displayedVersion = customProps && "changelogVersion" in customProps ? customProps.changelogVersion : undefined;
  const handledChangelog = useRef<string | null>(null);

  useEffect(() => {
    if (!sessionActivated || !loggedIn) {
      return;
    }

    if (!isChangelogActive || !version) {
      handledChangelog.current = null;
      if (isVisible) {
        dispatch({ type: "setWindowVisibility", windowId: whatsNewWindowId, isVisible: false });
      }
      return;
    }

    if (loading) {
      return;
    }

    const changelogKey = JSON.stringify([version, contentLanguage]);
    if (handledChangelog.current === changelogKey) {
      return;
    }

    const previousChangelog = handledChangelog.current;
    // Record the decision before dispatching: count updates, dismissal and StrictMode must not reopen it.
    handledChangelog.current = changelogKey;
    if (!shouldShow) {
      return;
    }

    dispatch({
      type: "setWindowCustomProps",
      windowId: whatsNewWindowId,
      windowKind: WindowKind.WhatsNewWindow,
      customProps: { title: "What's New?", changelogVersion: version, onCheckboxClick: dontShowAgain },
    });

    if (!isVisible) {
      dispatch({ type: "setWindowVisibility", windowId: whatsNewWindowId, isVisible: true });
    }
    if (!isVisible || displayedVersion !== version || previousChangelog !== null) {
      incrementCount();
    }
  }, [
    sessionActivated,
    loggedIn,
    isChangelogActive,
    version,
    contentLanguage,
    loading,
    shouldShow,
    dispatch,
    isVisible,
    displayedVersion,
    incrementCount,
    dontShowAgain,
  ]);
}
